import { yupResolver } from '@hookform/resolvers/yup';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import { InvoiceLineItemType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { cloneDeep, startCase } from 'lodash-es';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';

import { TextFormField } from '~components/FormFields/TextFormField';
import { AddOnCharge } from '~hooks/useAddOns/models';
import {
  InvoiceLineItem,
  PayCommissionPresentation,
} from '~hooks/useInvoiceLineItems/models';
import { Invoice } from '~hooks/useInvoices/models';
import { Job } from '~hooks/useJob/models';
import { DriverPayUtils } from '~pages/Approvals/DriverPay/driverPayUtils';
import { useStores } from '~store';
import { toDecimal } from '~utils/decimal';

import AddOnChargesTableRows, { AddOnChargesFormHandler } from './AddOnChargesTableRows';
import {
  LineItemsTableCell,
  LineItemsTableCellProps,
  LineItemsTableTableRow,
  NetRateCells,
  withNetColumnHeaders,
} from './components/lineItemsTableComponents';
import {
  decimalMultiply,
  isLineEditable,
  LoadLineItemDTO,
  loadlLineItemSchema,
} from './helpers';
import { LineItemsFormHandler } from './LineItemsFormHandler';
import { LineItemStrategy, LoadLineItemConcreteStrategy } from './LineItemStrategy';

const headers: Array<{
  text: string;
  textAlign: LineItemsTableCellProps['textAlign'];
  width?: string;
  minWidth?: string;
}> = [
  {
    text: 'Line Item',
    textAlign: 'left',
    width: '100%',
    minWidth: '120px',
  },
  {
    text: `${t('form_fields.material')}`,
    textAlign: 'right',
    width: '120px',
  },
  {
    text: `${t('approvals.driver_pay.tabs.driver_pay.truck_type')}`,
    textAlign: 'right',
    width: '150px',
  },
  {
    text: `${t('approvals.driver_pay.settlements.pdf.type')}`,
    textAlign: 'right',
    width: '80px',
  },
  {
    text: `${t('form_fields.unit')}`,
    textAlign: 'right',
    width: '80px',
  },
  {
    text: `${t('approvals.driver_pay.tabs.driver_pay.total_quantity')}`,
    textAlign: 'right',
    width: '90px',
  },
  {
    text: `${t('common.rate')}`,
    textAlign: 'right',
    width: '100px',
  },
  {
    text: `${t('common.total')}`,
    textAlign: 'right',
    width: '100px',
  },
];

const lineItemsSchema = yup.object().shape({
  lineItems: yup.array().of(loadlLineItemSchema),
});

type LineItemsDTO = yup.InferType<typeof lineItemsSchema>;

interface LoadRateLineItemsTableProps {
  addOnCharges: AddOnCharge[];
  invoice: Invoice;
  isEditing?: boolean;
  isUpdating?: boolean;
  job: Job;
  lineItems: InvoiceLineItem[];
  netRatePresentation?: PayCommissionPresentation;
  utils: DriverPayUtils;
}

const LoadRateLineItemsTable = forwardRef<
  LineItemsFormHandler,
  LoadRateLineItemsTableProps
>(function LoadRateLineItemsTable(
  {
    addOnCharges,
    invoice,
    isEditing,
    isUpdating,
    job,
    lineItems,
    netRatePresentation,
    utils,
  },
  ref,
) {
  const invoiceId = String(invoice.id);
  const strategy = useRef(LineItemStrategy.create(invoice))
    .current as LoadLineItemConcreteStrategy;
  const { invoiceLineItemsStore } = useStores();

  const addOnChargesRef = useRef<AddOnChargesFormHandler>({});

  const initialLineItems = useMemo(() => {
    return lineItems.map((lineItem) => {
      return {
        id: lineItem.id,
        quantity: lineItem.quantity.toString(),
        rate: (lineItem.rate ?? 0).toString(),
      } as unknown as LoadLineItemDTO;
    });
  }, [lineItems]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LineItemsDTO>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(lineItemsSchema),
    defaultValues: { lineItems: cloneDeep(initialLineItems) },
  });

  useFieldArray({ control, name: 'lineItems' });
  const watchedLineItems = useWatch({ control, name: 'lineItems' });

  const consolidatedLineItemsList = useMemo(() => {
    if (isEditing) {
      return lineItems.map((lineItem, index) => {
        return strategy.consolidate(lineItem, watchedLineItems?.[index]);
      });
    }

    return lineItems;
  }, [isEditing, lineItems, watchedLineItems]);

  const lineItemsById = useMemo(
    () => new Map(lineItems.map((item) => [item.id, item])),
    [lineItems],
  );

  useEffect(() => {
    let anyHasEdits = false;
    const invoiceSubtotal = consolidatedLineItemsList.reduce((sum, lineItem) => {
      const original = lineItemsById.get(lineItem.id);
      const hasEdits =
        !original ||
        lineItem.quantity !== original.quantity ||
        (lineItem.rate ?? 0) !== (original.rate ?? 0);

      if (hasEdits) anyHasEdits = true;

      return sum + (hasEdits ? strategy.calculateTotal(lineItem) : original.totalAmount);
    }, 0);

    const freightLineItems = consolidatedLineItemsList.filter(
      (lineItem) =>
        lineItem.lineItemType === InvoiceLineItemType.INVOICE_LINE_ITEM ||
        lineItem.lineItemType === InvoiceLineItemType.FREIGHT_RATE_LINE_ITEM,
    );

    const freightTotal = freightLineItems.reduce((sum, lineItem) => {
      return sum + decimalMultiply(lineItem.quantity, invoice.rateValue ?? 0);
    }, 0);

    const freightQuantity = freightLineItems
      .reduce((sum, lineItem) => sum.plus(toDecimal(lineItem.quantity)), toDecimal(0))
      .toNumber();

    invoiceLineItemsStore.setInvoiceSubtotalByInvoiceId(invoiceId, invoiceSubtotal);
    invoiceLineItemsStore.setFreightTotalByInvoiceId(invoiceId, freightTotal);
    invoiceLineItemsStore.setFreightQuantityByInvoiceId(invoiceId, freightQuantity);
    invoiceLineItemsStore.setLineItemsHasEditsByInvoiceId(invoiceId, anyHasEdits);
  }, [consolidatedLineItemsList, invoiceId, lineItemsById]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit: () => {
        return new Promise((resolve, reject) => {
          handleSubmit(
            async (data) => {
              try {
                const lineItemsToSubmit = strategy.diffItemsCollection(
                  initialLineItems as unknown as (LoadLineItemDTO & { id: string })[],
                  data.lineItems ?? [],
                );

                const addOnChargesToSubmit =
                  (await addOnChargesRef.current.onSubmit?.()) ?? [];

                resolve({
                  lineItems: lineItemsToSubmit,
                  addOnCharges: addOnChargesToSubmit,
                });
              } catch (error) {
                reject(error);
              }
            },
            (errors) => {
              reject(errors);
            },
          )();
        });
      },
    }),
    [initialLineItems],
  );

  const columns = withNetColumnHeaders(headers, !!netRatePresentation);

  return (
    <>
      <TableHead>
        <LineItemsTableTableRow isHeader>
          {columns.map(({ text, width, ...rest }) => (
            <LineItemsTableCell key={text} isHeader width={width} {...rest}>
              {text}
            </LineItemsTableCell>
          ))}
        </LineItemsTableTableRow>
      </TableHead>

      <TableBody>
        {consolidatedLineItemsList.map((lineItem, index) => {
          const original = lineItemsById.get(lineItem.id);
          const isRowEditable =
            isEditing && isLineEditable(original ?? lineItem, invoice);
          const hasEdits =
            !original ||
            lineItem.quantity !== original.quantity ||
            (lineItem.rate ?? 0) !== (original.rate ?? 0);
          const lineItemTotal = hasEdits
            ? strategy.calculateTotal(lineItem)
            : original.totalAmount;
          const lineItemNet = netRatePresentation?.lineItemNet(lineItem.id);

          return (
            <LineItemsTableTableRow key={lineItem.id}>
              <LineItemsTableCell textAlign={headers[0].textAlign}>
                {lineItem.name}
              </LineItemsTableCell>

              <LineItemsTableCell textAlign={headers[1].textAlign}>
                {lineItem.material?.name}
              </LineItemsTableCell>

              <LineItemsTableCell textAlign={headers[2].textAlign}>
                {job.equipment?.equipmentType.name}
              </LineItemsTableCell>

              <LineItemsTableCell textAlign={headers[3].textAlign}>
                {lineItem.lineItemType === InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM
                  ? t('approvals.driver_pay.settlements.pdf.material')
                  : t('approvals.driver_pay.settlements.pdf.freight')}
              </LineItemsTableCell>

              <LineItemsTableCell textAlign={headers[4].textAlign}>
                {lineItem.lineItemType === InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM
                  ? startCase(lineItem.materialRateUnitOfMeasure ?? '')
                  : t('administration.rates.rate_per_load')}
              </LineItemsTableCell>

              <LineItemsTableCell textAlign={headers[5].textAlign}>
                {isRowEditable ? (
                  <TextFormField
                    control={control}
                    errors={errors}
                    type="number"
                    name={`lineItems[${index}].quantity`}
                    sx={{
                      ml: 'auto',
                      '& .MuiFormControl-root': { mb: 0 },
                      '& .MuiOutlinedInput-root': { pr: 0 },
                      '& .MuiOutlinedInput-input': { p: '5px 7px', fontSize: '12px' },
                    }}
                    inputProps={{ sx: { textAlign: 'right' } }}
                    disabled={isUpdating}
                  />
                ) : (
                  lineItem.quantity
                )}
              </LineItemsTableCell>

              <LineItemsTableCell textAlign={headers[6].textAlign}>
                {isRowEditable ? (
                  <TextFormField
                    control={control}
                    errors={errors}
                    type="number"
                    name={`lineItems[${index}].rate`}
                    sx={{
                      ml: 'auto',
                      '& .MuiFormControl-root': { mb: 0 },
                      '& .MuiOutlinedInput-root': { pr: 0 },
                      '& .MuiOutlinedInput-input': { p: '5px 7px', fontSize: '12px' },
                    }}
                    inputProps={{ sx: { textAlign: 'right' } }}
                    disabled={isUpdating}
                  />
                ) : (
                  utils.currencyFormatter(lineItem.rate ?? 0)
                )}
              </LineItemsTableCell>

              {netRatePresentation && (
                <NetRateCells
                  currencyFormatter={utils.currencyFormatter}
                  lineItemNet={lineItemNet}
                />
              )}
              <LineItemsTableCell textAlign={headers[7].textAlign}>
                {utils.currencyFormatter(lineItemNet?.netTotalAmount ?? lineItemTotal)}
              </LineItemsTableCell>
            </LineItemsTableTableRow>
          );
        })}

        <AddOnChargesTableRows
          ref={addOnChargesRef}
          key={addOnCharges
            .map((addOnCharge) => `${addOnCharge.id}-${addOnCharge.totalAmount}`)
            .join()}
          addOnCharges={addOnCharges}
          invoice={invoice}
          isEditing={isEditing}
          isUpdating={isUpdating}
          lineItemColSpan={1}
          lineItemsTableColumnsCount={headers.length}
          showsNetColumns={!!netRatePresentation}
          utils={utils}
        />
      </TableBody>
    </>
  );
});

export default LoadRateLineItemsTable;
